/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import { useEffect } from 'react';
import { useShadowRootElements } from '@backstage/plugin-techdocs-react';
import { useLocation } from 'react-router-dom';
import { useStarredDocs } from './hooks';

/**
 * @internal
 * This function returns an implementation of visitName which is responsible
 * for receiving a pathname and returning a string (name).
 */
const getDocPageEntityName = () => {
  // Fallback to document title
  return global.document.title.split(' | ')?.[0];
};

const getDocRefKey = (pathname: string, pageTitle: string) => {
  // If it is a catalog entity or docs page, get the key from the path
  const regex =
    /^\/(?:catalog|docs)\/(?<namespace>[^\/]+)\/(?<kind>[^\/]+)\/(?<name>[^\/]+)/;
  const result = regex.exec(pathname);
  if (result && result?.groups)
    return `${result.groups.kind}:${result.groups.namespace}/${result.groups.name}/${pageTitle}`
      .toLowerCase()
      .replace(/ /g, '');
  return pathname.toLowerCase();
};

const starIcon =
  '<svg focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z"></path></svg>';
const starIconYellow =
  '<svg focusable="false" viewBox="0 0 24 24" aria-hidden="false"><path fill="#f3ba37" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"></path></svg>';

export const BookmarkDocLinkAddon = () => {
  const { toggleStarredDoc, isStarredDoc } = useStarredDocs();
  const { pathname } = useLocation();
  const docContentEl = useShadowRootElements<HTMLDivElement>([
    'article.md-content__inner',
  ]);
  const bookmarkedTitleAdd = 'Add to favorites';
  const bookmarkedTitleRemove = 'Remove from favorites';

  useEffect(() => {
    if (!docContentEl || !docContentEl[0]) {
      return;
    }
    // Get bookmark doc icon btn if it is exist
    const bookmarkDocLinkElExisting =
      docContentEl[0].getElementsByClassName('bookmark-doc-link');
    let bookmarkDocLinkEl;
    // get the page title before which we need to add star icon element
    const pageTitleEl: HTMLHeadingElement =
      docContentEl[0].getElementsByTagName('h1')?.[0];
    const pageTitle =
      pageTitleEl.firstChild?.nodeValue || pageTitleEl.innerText;
    if (pageTitleEl && bookmarkDocLinkElExisting.length <= 0) {
      // Add new element with click event for bookmark doc icon btn
      bookmarkDocLinkEl = document.createElement('div');
      bookmarkDocLinkEl.classList.add(
        'bookmark-doc-link',
        'md-content__button',
        'md-icon',
      );
      bookmarkDocLinkEl.style.cursor = 'pointer';
      bookmarkDocLinkEl.addEventListener('click', _event => {
        toggleStarredDoc({
          key: getDocRefKey(pathname, pageTitle),
          name: `${getDocPageEntityName()} | ${pageTitle}`,
          path: pathname,
        });
      });
      pageTitleEl.insertAdjacentElement('beforebegin', bookmarkDocLinkEl);
    } else {
      bookmarkDocLinkEl = bookmarkDocLinkElExisting[0] as HTMLDivElement;
    }
    // Show yellow icon if page is bookmarked earlier
    if (isStarredDoc(getDocRefKey(pathname, pageTitle))) {
      bookmarkDocLinkEl.innerHTML = starIconYellow;
      bookmarkDocLinkEl.title = bookmarkedTitleRemove;
    } else {
      bookmarkDocLinkEl.innerHTML = starIcon;
      bookmarkDocLinkEl.title = bookmarkedTitleAdd;
    }
  }, [docContentEl, pathname, toggleStarredDoc, isStarredDoc]);

  return null;
};
